import {
  ChevronDownIcon,
  FolderInputIcon,
  HistoryIcon,
  MoreHorizontalIcon,
  PencilIcon,
  Trash2Icon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/** 「移動先」の選択肢（別のグループ、またはグループ未指定）。 */
export interface TabMoveTarget {
  value: string;
  label: string;
}

function MoveTargetItems({
  moveTargets,
  onMoveToGroup,
}: {
  moveTargets: TabMoveTarget[];
  onMoveToGroup: (value: string) => void;
}) {
  return (
    <>
      <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground">
        移動先のグループ
      </DropdownMenuLabel>
      {moveTargets.map((target) => (
        <DropdownMenuItem key={target.value} onSelect={() => onMoveToGroup(target.value)}>
          {target.label}
        </DropdownMenuItem>
      ))}
    </>
  );
}

export interface TabRowMenuProps {
  tabName: string;
  /** 指定すると「別のグループへ移動」のサブメニューを出す（グループ未指定の行は専用のボタンで出すため渡さない）。 */
  moveTargets?: TabMoveTarget[];
  isLayoutBusy: boolean;
  onStartRename: () => void;
  onHistoryRequest: () => void;
  onMoveToGroup: (value: string) => void;
  onDeleteRequest: () => void;
}

/** タブ行の「⋯」メニュー。常に出さない操作（名前変更・履歴・グループ間の移動・削除）をまとめる。 */
export function TabRowMenu({
  tabName,
  moveTargets,
  isLayoutBusy,
  onStartRename,
  onHistoryRequest,
  onMoveToGroup,
  onDeleteRequest,
}: TabRowMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          className="size-9"
          aria-label={`${tabName}のその他の操作`}
        >
          <MoreHorizontalIcon size={16} aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onSelect={onStartRename}>
          <PencilIcon aria-hidden className="text-muted-foreground" />
          名前を変更
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onHistoryRequest}>
          <HistoryIcon aria-hidden className="text-muted-foreground" />
          履歴とプレビュー
        </DropdownMenuItem>
        {moveTargets !== undefined && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger disabled={moveTargets.length === 0 || isLayoutBusy}>
              <FolderInputIcon aria-hidden className="text-muted-foreground" />
              別のグループへ移動
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-52">
              <MoveTargetItems moveTargets={moveTargets} onMoveToGroup={onMoveToGroup} />
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={isLayoutBusy}
          onSelect={onDeleteRequest}
          className="text-danger focus:bg-danger-muted focus:text-danger"
        >
          <Trash2Icon aria-hidden />
          削除
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface TabMoveMenuProps {
  tabName: string;
  moveTargets: TabMoveTarget[];
  isLayoutBusy: boolean;
  onMoveToGroup: (value: string) => void;
}

/** グループ未指定のタブ行に常に出す「グループへ移動」。この行で最初にすべき操作なので「⋯」に隠さない。 */
export function TabMoveMenu({
  tabName,
  moveTargets,
  isLayoutBusy,
  onMoveToGroup,
}: TabMoveMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          aria-label={`${tabName}を別のグループへ移動`}
          disabled={moveTargets.length === 0 || isLayoutBusy}
          className="border-warning/40"
        >
          <FolderInputIcon size={16} aria-hidden />
          グループへ移動
          <ChevronDownIcon size={16} aria-hidden className="text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <MoveTargetItems moveTargets={moveTargets} onMoveToGroup={onMoveToGroup} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
